import { Component, OnInit, ViewChild, OnDestroy } from "@angular/core";
import { AuthService, UserPermissions } from "../../services/auth.service";
import { LowyGridComponent } from "../../components/grid/grid.component";
import { ReportService } from "../../services/report.service";
import { Upload } from "../../models/upload.model";
import { ApprovalService } from "../../services/approval.service";
import { DataService } from "../../services/data.service";
import { ProfileService } from "../../services/profile.service";
import { ToastService } from "../../components/toast/toast.service";
import { LoadingSpinnerService } from "../../components/loading-spinner/loading-spinner.service";
import Utils from "../../services/utils";
import { RejectUploadComponent } from "./reject-modal.component";
import * as moment from "moment";
import * as _ from "underscore";
import { ImageMetaComponent } from "../uploader/modals/image-meta-modal.component";
import { UploaderService } from "../../services/uploader.service";
import { UploadingFile } from "../uploader/uploader.component";
import { GuidService } from "../../services/guid.service";
import { Bucket } from "../../models/bucket.enum";
import { DeleteConfirmationModalComponent } from "../../components/modal/modals/delete-confirmation-modal.component";
import { Role } from "../../models/role.enum";
import { Patient } from "../../models/patient.model";
import { AdjudicateComponent } from "../grading/adjudication/adjudication-modal.component";
import { MactelDiagnosis } from "../../models/adjudication.model";
import { PackageCommentsComponent } from "./package-comments-modal.component";
import { PatientService } from "../../services/patient.service";
import { Router } from "@angular/router";
import { ImagePackageService } from "../../services/image-package.service";
import {
  ImagePackage,
  ImagePackageStatus,
} from "../../models/image-package.model";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { GradingService } from "../../services/grading.service";
import {
  GradingStagingSheet,
  GradingStagingSheetStatus,
} from "../../models/grading.model";
import { ConfirmDatalockModalComponent } from "./modals/confirm-datalock-modal.component";
import { DataShareService } from "../../services/data-share.service";
import { ApproveUploadComponent } from "./approve-modal.component";

@Component({
  templateUrl: "./reports.component.html",
  styleUrls: ["./reports.component.scss"],
  providers: [
    AuthService,
    ReportService,
    ApprovalService,
    DataService,
    ProfileService,
    UploaderService,
    GuidService,
    PatientService,
    ImagePackageService,
    GradingService,
  ],
})
export class ReportsComponent implements OnInit, OnDestroy {
  public reports: Report[];
  private allReports: { [reportId: string]: Report };
  public currentReport: string;
  private permissions: UserPermissions;
  private role: Role;

  public pendingApprovals: Array<Upload>;
  private userLookup: { [userId: number]: string } = {};

  public resubmittingUpload: Upload; // old upload (the one you're resubmitting for)
  private newUploadGuid: string; // new upload's s3 guid (for lookup server side)
  private resubmittingFileName: string;
  public fileInputForm;
  public fileInput;

  private dataLockColumns;
  private dataLockColumnHeaders;
  private dataLockAllData;
  private dataLockToDate;
  private filteredDataLockData: Array<ImagePackage>;
  private showOnlyIncompleteDataLock: boolean;

  @ViewChild("reportGrid") reportGrid: LowyGridComponent;
  @ViewChild("approvalGrid") approvalGrid: LowyGridComponent;
  @ViewChild("adjudicateGrid") adjudicateGrid: LowyGridComponent;
  @ViewChild("packageManagementGrid") packageManagementGrid: LowyGridComponent;
  @ViewChild("readyToGradeGrid") readyToGradeGrid: LowyGridComponent;
  @ViewChild("dataLockGrid") dataLockGrid: LowyGridComponent;

  @ViewChild("imageMeta") imageMeta: ImageMetaComponent;
  @ViewChild("lowyRejectUpload") lowyRejectUpload: RejectUploadComponent;
  @ViewChild("deleteAwaitingReupload")
  deleteAwaitingReupload: DeleteConfirmationModalComponent;
  @ViewChild("lowyAdjudicate") lowyAdjudicate: AdjudicateComponent;
  @ViewChild("lowyPackageComments")
  lowyPackageComments: PackageCommentsComponent;

  @ViewChild("lowyConfirmDatalockModal")
  lowyConfirmDatalockModal: ConfirmDatalockModalComponent;

  @ViewChild("lowyApproveUpload") lowyApproveUpload: ApproveUploadComponent;
  private refreshTimer;
  public updateSidebar: any; // used to trigger pipe update

  public itemsCheckedCount: number;

  constructor(
    private authService: AuthService,
    private reportService: ReportService,
    private approvalService: ApprovalService,
    private toastService: ToastService,
    private loadingSpinnerService: LoadingSpinnerService,
    private uploadService: UploaderService,
    private profileService: ProfileService,
    private guidService: GuidService,
    private patientService: PatientService,
    private router: Router,
    private imagePackageService: ImagePackageService,
    private gradingService: GradingService,
    private dataShareService: DataShareService
  ) {
    this.reports = [];
    this.allReports = {
      uploadSummary: {
        name: "Upload Summary",
        id: "uploadSummary",
        icon: "view_list",
        group: "Image Review",
      },
      pendingApprovals: {
        name: "Pending Approvals",
        id: "pendingApprovals",
        icon: "image",
        group: "Image Review",
      },
      awaitingReupload: {
        name: "Awaiting Re-Upload",
        id: "awaitingReupload",
        icon: "alarm_on",
        group: "Image Review",
      },
      myUploads: {
        name: "Uploads",
        id: "myUploads",
        icon: "file_upload",
        group: "Image Review",
      },
      packageManagementQueue: {
        name: "Package Management Queue",
        id: "packageManagementQueue",
        icon: "assessment",
        group: "Image Review",
      },

      adjudicationQueue: {
        name: "Diagnosis Queue",
        id: "adjudicationQueue",
        icon: "bookmark",
        group: "MacTel Diagnosis",
      },
      diagnosisReport: {
        name: "Diagnosis Report",
        id: "diagnosisReport",
        icon: "list",
        group: "MacTel Diagnosis",
      },

      readyToGrade: {
        name: "Ready To Grade",
        id: "readyToGrade",
        icon: "check",
        group: "Grading",
      },
      escalatedToSrGrader: {
        name: "Escalated to Sr Grader",
        id: "escalatedToSrGrader",
        icon: "unfold_more",
        group: "Grading",
      },
      escalatedToClinicianReview: {
        name: "Escalated to Clinician Review",
        id: "escalatedToClinicianReview",
        icon: "more",
        group: "Grading",
      },
      dataLock: {
        name: "Data Lock",
        id: "dataLock",
        icon: "lock_open",
        group: "Grading",
      },
    };
  }

  async ngOnInit() {
    this.authService.permissions$.subscribe(async (permissions) => {
      this.permissions = permissions;
      this.role = await this.authService.getRole();
      let reportToView;
      this.updateReportCounts();
      if (permissions.managePendingApprovals) {
        this.reports.push(this.allReports.uploadSummary);
        this.reports.push(this.allReports.pendingApprovals);
        this.reports.push(this.allReports.awaitingReupload);

        this.reports.push(this.allReports.packageManagementQueue);

        reportToView = this.allReports.pendingApprovals;
      }
      if (permissions.uploadData) {
        this.reports.push(this.allReports.myUploads);
        if (!permissions.managePendingApprovals) {
          reportToView = this.allReports.myUploads;
        }
      }
      if (permissions.mactelDiagnosis) {
        this.reports.push(this.allReports.diagnosisReport);
      }
      if (permissions.adjudicate) {
        this.reports.push(this.allReports.adjudicationQueue);
        if (this.role !== Role.grader) {
          reportToView = this.allReports.adjudicationQueue;
        }
      }
      if (permissions.grade) {
        this.reports.push(this.allReports.readyToGrade);
        if (this.role === Role.grader) {
          reportToView = this.allReports.readyToGrade;
        }
      }
      if (this.role === Role.admin) {
        reportToView = this.allReports.uploadSummary;
        this.reports.push(this.allReports.escalatedToSrGrader);
        this.reports.push(this.allReports.escalatedToClinicianReview);
      }
      if (permissions.srGrader) {
        this.reports.push(this.allReports.escalatedToSrGrader);
      }
      if (permissions.clinicianReview) {
        this.reports.push(this.allReports.escalatedToClinicianReview);
      }
      if (permissions.dataLock) {
        this.reports.push(this.allReports.dataLock);
      }
      if (this.dataShareService.reportPage.lastReport) {
        reportToView =
          this.allReports[this.dataShareService.reportPage.lastReport];
      }
      if (this.role === Role.technician) {
        this.reports.push(this.allReports.diagnosisReport);
      }
      this.viewReport(reportToView);
      this.dataShareService.reportPage.lastReport = null;
      this.updateSidebar = new Date(); // trigger a pipe update (it's not impure on purpose..)
    });

    this.fileInput = document.getElementById("fileInput");
    this.fileInputForm = document.getElementById("fileInputForm");

    this.startRefreshTimer();
  }

  private async startRefreshTimer() {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(async () => {
      await this.viewReport(this.allReports[this.currentReport], false);
      this.updateReportCounts();
    }, 900000); // 15min
  }

  ngOnDestroy() {
    clearTimeout(this.refreshTimer);
  }

  private async updateReportCounts() {
    this.startRefreshTimer();
    if (this.permissions.managePendingApprovals) {
      this.pendingApprovals = await this.approvalService.getPendingApprovals();
      this.allReports.pendingApprovals.count = this.pendingApprovals.length;

      const awaitingReupload = await this.reportService.getReport(
        "awaitingReupload"
      );
      this.allReports.awaitingReupload.count = awaitingReupload.length;

      const packageManagementQueue = await this.reportService.getReport(
        "packageManagementQueue"
      );
      this.allReports.packageManagementQueue.count =
        packageManagementQueue.length;
    }
    if (this.permissions.uploadData) {
      let myUploads = await this.reportService.getReport("myUploads");
      myUploads = _.filter(myUploads, (upload) => upload.status === "denied");
      this.allReports.myUploads.count = myUploads.length;
    }
    if (this.permissions.mactelDiagnosis) {
      const diagnosisReport = await this.reportService.getReport(
        "diagnosisReport"
      );
      this.allReports.diagnosisReport.count = diagnosisReport.length;
      const readyToGrade = await this.reportService.getReport("readyToGrade");
      this.allReports.readyToGrade.count = readyToGrade.length;
    }
    if (this.permissions.adjudicate) {
      const adjudicationQueue = await this.reportService.getReport(
        "adjudicationQueue"
      );
      this.allReports.adjudicationQueue.count = adjudicationQueue.length;
    }
    if (this.permissions.dataLock) {
      const dataLock = await this.reportService.getReport("dataLock");
      this.allReports.dataLock.count = dataLock.length;
    }

    if (this.permissions.srGrader) {
      const escalatedToSrGrader = await this.reportService.getReport(
        "escalatedToSrGrader"
      );
      this.allReports.escalatedToSrGrader.count = escalatedToSrGrader.length;
    }
    if (this.permissions.clinicianReview) {
      const escalatedToClinicianReview = await this.reportService.getReport(
        "escalatedToClinicianReview"
      );
      this.allReports.escalatedToClinicianReview.count =
        escalatedToClinicianReview.length;
    }

    this.updateSidebar = new Date(); // trigger a pipe update (it's not impure on purpose..)
  }

  public async viewReport(report: Report, showSpinner = true) {
    if (showSpinner) {
      this.loadingSpinnerService.start();
    }
    this.currentReport = report.id;
    await Utils.sleep(); // allow time for grids to ngIf
    let columns: string[];
    let columnHeaders: string[];
    let data: any[];

    switch (report.id) {
      case "uploadSummary":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "site",
          "photoDate",
          "filesSubmitted",
          "filesAccepted",
          "filesRejected",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Site",
          "Photo Date",
          "Files submitted",
          "Files accepted",
          "Files rejected",
        ];
        data = await this.reportService.getReport(report.id);
        this.reportGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;

      case "awaitingReupload":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "uploadDate",
          "technician",
          "folderName",
          "imageType",
          "reason",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Upload Date",
          "Technician",
          "Folder Name",
          "Image Type",
          "Rejection Reasons",
        ];
        data = await this.reportService.getReport(report.id);
        this.allReports.awaitingReupload.count = data.length;
        this.reportGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;

      case "myUploads":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "studyId",
          "uploadDate",
          "fileName",
          "imageType",
          "status",
          "uploadTechnician",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Study ID",
          "Upload Date",
          "File Name",
          "Image Type",
          "Status",
          "Upload Technician",
        ];
        data = await this.reportService.getReport(report.id);
        const showAll = this.reportGrid.toggleCheckbox;
        if (!showAll) {
          data = _.filter(data, (upload) => upload.status === "denied");
        }
        data = _.map(data, (upload) => {
          switch (upload.status) {
            case "pendingApproval":
              upload.status = "Pending Approval";
              break;
            case "approved":
              upload.status = "Approved";
              break;
            case "denied":
              upload.status = "Denied";
              break;
            case "resubmitted":
              upload.status = "Resubmitted";
              break;
          }
          return upload;
        });
        this.allReports.myUploads.count = data.length;
        this.reportGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;

      case "pendingApprovals":
        this.pendingApprovals =
          await this.approvalService.getPendingApprovals();
        this.allReports.pendingApprovals.count = this.pendingApprovals.length;
        columns = [
          "checkbox",
          "dataType",
          "patientId",
          "visitId",
          "fileName",
          "imageType",
          "imagePlatform",
          "studyId",
          "comments",
          "uploadComments",
          "status",
          "uploadDate",
          "photoDate",
          "uploadUser",
        ];
        columnHeaders = [
          "checkbox",
          "dataType",
          "Patient ID",
          "Visit ID",
          "File name",
          "Image Type",
          "Image Platform",
          "Study ID",
          "Comments",
          "Upload Comments",
          "Status",
          "Upload Date",
          "Photo Date",
          "Upload User",
        ];

        data = [];
        for (let i = 0; i < this.pendingApprovals.length; i++) {
          const pendingApproval = this.pendingApprovals[i];
          let uploadUser: string =
            this.userLookup[pendingApproval.uploadUserId];
          if (!uploadUser) {
            try {
              uploadUser = await this.profileService.lookupUsername(
                pendingApproval.uploadUserId
              );
            } catch (err) {
              uploadUser = "Unknown";
            }
            this.userLookup[pendingApproval.uploadUserId] = uploadUser;
          }
          const metaData = JSON.parse(pendingApproval.metaData);

          data.push({
            checkbox: "0",
            dataType: pendingApproval.dataType,
            patientId: pendingApproval.patientId,
            visitId: pendingApproval.visitId,
            fileName: pendingApproval.name,
            imageType: metaData["IMAGE_TYPE"],
            imagePlatform: metaData["IMAGE_PLATFORM"],
            studyId: metaData["STUDY_ID"],
            comments: metaData["COMMENTS"],
            uploadComments: metaData["UPLOAD_COMMENTS"],
            uploadDate: moment(pendingApproval.createdAt).format(
              "MMM DD, YYYY"
            ),
            photoDate: moment(metaData["PHOTO_DATE"]).format("MMM DD, YYYY"),
            uploadUser: uploadUser,
            _id: pendingApproval.uploadId,
            status: pendingApproval.resubmissionForUploadId
              ? "Resubmission"
              : "New Upload",
          });
        }
        if (this.approvalGrid) {
          this.approvalGrid.dataReady(data, columns, columnHeaders);
        }
        this.loadingSpinnerService.stop();
        break;

      case "adjudicationQueue":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "studyId",
          "fp",
          "faf",
          "oct",
          "fa",
          "octType",
          "createdAt",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Study ID",
          "FP",
          "FAF",
          "OCT",
          "FA",
          "OCT Type",
          "Upload Date",
        ];
        data = await this.reportService.getReport(report.id);
        this.allReports.adjudicationQueue.count = data.length;
        if (this.adjudicateGrid) {
          this.adjudicateGrid.dataReady(data, columns, columnHeaders);
        }
        this.loadingSpinnerService.stop();
        break;

      case "packageManagementQueue":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "studyId",
          "fp",
          "faf",
          "oct",
          "fa",
          "octType",
          "createdAt",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Study ID",
          "FP",
          "FAF",
          "OCT",
          "FA",
          "OCT Type",
          "Approval Date",
        ];
        data = await this.reportService.getReport(report.id);
        this.allReports.packageManagementQueue.count = data.length;
        this.packageManagementGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;

      case "diagnosisReport":
        columns = [
          "checkbox",
          "patientId",
          "decision",
          "subStudy",
          "blrPattern",
          "blrEye",
          "reviewer",
          "notes.body",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "MacTel Diagnosis",
          "Sub Study",
          "Presence of MacTel BLR Pattern",
          "BLR Eye",
          "Reviewer",
          "Comments",
        ];
        data = await this.reportService.getReport(report.id);
        _.map(data, (item) => {
          item["decision"] = MactelDiagnosis[item["mactelDiagnosis"]];
        });
        this.allReports.diagnosisReport.count = data.length;
        this.reportGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;

      case "readyToGrade":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "studyId",
          "CF-FFA",
          "OCT",
          "FAFGRADE",
          "submittedDate",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Study ID",
          "CF-FFA",
          "OCT",
          "FAF Grade",
          "Approval Date",
        ];
        data = await this.reportService.getReport(report.id);
        data = _.map(data, (d) => {
          d["CF-FFA"] = GradingStagingSheetStatus[d["CF-FFA"]];
          d["OCT"] = GradingStagingSheetStatus[d["OCT"]];
          d["FAFGRADE"] = GradingStagingSheetStatus[d["FAFGRADE"]];
          return d;
        });
        this.allReports.readyToGrade.count = data.length;
        this.readyToGradeGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;

      case "dataLock":
        this.dataLockColumns = columns = [
          "checkbox",
          "patientId",
          "visitId",
          "studyId",
          "status",
          "submittedDate",
        ];
        this.dataLockColumnHeaders = columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Study ID",
          "Status",
          "Approval Date",
        ];
        data = await this.reportService.getReport(report.id);
        _.map(data, (item) => {
          item["status"] = ImagePackageStatus[item["status"]];
          if (item["status"] === "uploading") {
            item["status"] = "package incomplete";
          }
        });
        this.filteredDataLockData = this.dataLockAllData = data;
        this.allReports.dataLock.count = data.length;
        this.dataLockGrid.dataReady(data, columns, columnHeaders);
        this.setDataLockDates();
        this.filterDataLockData();
        this.loadingSpinnerService.stop();
        break;

      case "escalatedToSrGrader":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "studyId",
          "CF-FFA",
          "OCT",
          "FAFGRADE",
          "submittedDate",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Study ID",
          "CF-FFA",
          "OCT",
          "FAF Grade",
          "Approval Date",
        ];
        data = await this.reportService.getReport(report.id);
        data = _.map(data, (d) => {
          d["CF-FFA"] = GradingStagingSheetStatus[d["CF-FFA"]];
          d["OCT"] = GradingStagingSheetStatus[d["OCT"]];
          d["FAFGRADE"] = GradingStagingSheetStatus[d["FAFGRADE"]];
          return d;
        });
        this.allReports.escalatedToSrGrader.count = data.length;
        this.readyToGradeGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;

      case "escalatedToClinicianReview":
        columns = [
          "checkbox",
          "patientId",
          "visitId",
          "studyId",
          "CF-FFA",
          "OCT",
          "FAFGRADE",
          "submittedDate",
        ];
        columnHeaders = [
          "checkbox",
          "Patient ID",
          "Visit ID",
          "Study ID",
          "CF-FFA",
          "OCT",
          "FAF Grade",
          "Approval Date",
        ];
        data = await this.reportService.getReport(report.id);
        data = _.map(data, (d) => {
          d["CF-FFA"] = GradingStagingSheetStatus[d["CF-FFA"]];
          d["OCT"] = GradingStagingSheetStatus[d["OCT"]];
          d["FAFGRADE"] = GradingStagingSheetStatus[d["FAFGRADE"]];
          return d;
        });
        this.allReports.escalatedToClinicianReview.count = data.length;
        this.readyToGradeGrid.dataReady(data, columns, columnHeaders);
        this.loadingSpinnerService.stop();
        break;
    }
  }

  public openApproveModal() {
    const checkedData = this.approvalGrid.checkedItems();
    this.lowyApproveUpload.open(true, checkedData.length);
  }

  async approve(comment?: string) {
    this.loadingSpinnerService.start();
    const checkedData = this.approvalGrid.checkedItems();
    try {
      await this.approvalService.approve(_.pluck(checkedData, "_id"), comment);
      this.updateReportCounts();
      this.loadingSpinnerService.stop();
      this.viewReport(this.allReports.pendingApprovals);
    } catch (err) {
      this.toastService.throwToast("Error approving data: " + err.message);
      this.loadingSpinnerService.stop();
      this.viewReport(this.allReports.pendingApprovals);
    }
  }

  async rejectFiles() {
    const checkedData = this.approvalGrid.checkedItems();
    if (checkedData[0]) {
      this.lowyRejectUpload.open(0, checkedData[0]);
    }
  }

  async reject(e: {
    index: number;
    item: any;
    rejectionText: string;
    rejectionReasons: Object;
  }) {
    const checkedData = this.approvalGrid.checkedItems();
    try {
      await this.approvalService.reject(
        checkedData[e.index]._id,
        e.item,
        e.rejectionText,
        e.rejectionReasons
      );
      const newIndex = e.index + 1;
      if (checkedData[newIndex]) {
        await Utils.sleep(500);
        this.lowyRejectUpload.open(newIndex, checkedData[newIndex]);
      } else {
        this.updateReportCounts();
        this.viewReport(this.allReports.pendingApprovals);
      }
    } catch (err) {
      this.toastService.throwToast("Error rejecting data: " + err.message);
      this.viewReport(this.allReports.pendingApprovals);
    }
  }

  clearAwaitingReupload(upload: Upload) {
    this.deleteAwaitingReupload.open({
      data: upload,
      headline: "Delete " + upload["folderName"],
      subheading:
        "Are you sure you want to delete the upload " +
        upload["folderName"] +
        "?",
    });
  }

  async confirmDeleteAwaitingReupload(upload: Upload) {
    try {
      await this.approvalService.clearAwaitingReupload(upload.uploadId);
      this.updateReportCounts();
      this.viewReport(this.allReports.awaitingReupload);
    } catch (err) {
      this.toastService.throwToast("Error clearing upload: " + err.message);
      this.viewReport(this.allReports.awaitingReupload);
    }
  }

  resubmitUpload(upload: Upload) {
    this.resubmittingUpload = upload;
    this.fileInput.click();
  }

  async uploadResubmission(fileToUpload: File) {
    this.loadingSpinnerService.start();
    const guid = this.guidService.getGuid();
    this.newUploadGuid = guid;
    this.resubmittingFileName = fileToUpload.name;

    const temporaryUploadInfo = await this.uploadService.getTemporaryUploadInfo(
      guid
    );

    const emitter = this.uploadService
      .temporaryUpload(fileToUpload, guid, temporaryUploadInfo.form)
      .subscribe(async (e) => {
        this.fileInputForm.reset();
        if (typeof e === "number") {
          this.loadingSpinnerService.start(e);
        } else if (!e) {
          // complete
          this.loadingSpinnerService.stop();
          emitter.unsubscribe();
          this.fileInputForm.reset();

          this.resubmittingUpload = await this.uploadService.getUploadRecord(
            this.resubmittingUpload.uploadId
          );
          const meta = JSON.parse(this.resubmittingUpload.metaData);
          const imageFile = {
            patientId: this.resubmittingUpload.patientId,
            site: meta["SITE"],
            datePhotoTaken: meta["PHOTO_DATE"],
            eye: meta["EYE"],
            imageType: meta["IMAGE_TYPE"],
            imagePlatform: meta["IMAGE_PLATFORM"],
            studyId: meta["STUDY_ID"],
            status: this.resubmittingUpload.status,
            rejectionReasons: JSON.parse(
              this.resubmittingUpload["rejectionReasons"]
            ),
            name: this.resubmittingUpload.name,
          };
          this.imageMeta.open(imageFile);
        } else {
          // error
          this.loadingSpinnerService.stop();
          this.toastService.throwToast("Error uploading file: " + e);
          emitter.unsubscribe();
        }
      });
  }

  async resubmitSaved(file: UploadingFile) {
    this.loadingSpinnerService.start();
    try {
      file._id = this.newUploadGuid;
      file.visitId = this.resubmittingUpload.visitId;
      file.fileName = this.resubmittingFileName;
      file.dataType = "image";
      const newUpload = await this.uploadService.submit(file);
      await this.uploadService.saveResubmission(
        this.resubmittingUpload.uploadId,
        newUpload.uploadId
      );
      this.updateReportCounts();
      this.loadingSpinnerService.stop();
      this.toastService.throwToast("Data successfully resubmitted");
      this.viewReport(this.allReports.myUploads);
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast(err.message);
    }
    this.imageMeta.clearSaved();
  }

  public adjudicatePatient(imagePackage: ImagePackage) {
    this.lowyAdjudicate.open(imagePackage);
  }

  public async packageComment(imagePackage: ImagePackage) {
    this.loadingSpinnerService.start();
    const comments = await this.imagePackageService.getImagePackageComments(
      imagePackage.imagePackageId
    );
    this.loadingSpinnerService.stop();
    this.lowyPackageComments.open(imagePackage, comments);
  }

  public async packageCommentsSaved(imagePackage: ImagePackage) {
    this.loadingSpinnerService.start();
    try {
      await this.imagePackageService.updateImagePackage(
        imagePackage,
        imagePackage.patientId,
        imagePackage.visitId,
        imagePackage.studyId
      );
      this.toastService.throwToast("Package successfully updated");
      this.viewReport(this.allReports.packageManagementQueue);
      this.updateReportCounts();
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast(err.message);
    }
  }

  public adjudicationSaved() {
    this.viewReport(this.allReports.adjudicationQueue);
    this.updateReportCounts();
  }

  public gradePatient(patient) {
    this.dataShareService.reportPage.lastReport = this.currentReport;
    this.router.navigate([
      "/grading",
      patient.patientId,
      patient["visitId"],
      patient["studyId"],
    ]);
  }

  public dataLockToDateChange(toDate: MatDatepickerInputEvent<string>) {
    this.dataLockToDate = moment(toDate.value);
    this.filterDataLockData();
  }

  private filterDataLockData() {
    this.filteredDataLockData = _.filter(this.dataLockAllData, (data) => {
      const toDate = this.dataLockToDate.endOf("day");
      if (toDate && toDate.isBefore(data["submittedDate"])) {
        return false;
      }
      if (
        this.showOnlyIncompleteDataLock &&
        [
          ImagePackageStatus.submitted,
          ImagePackageStatus.notGrading,
          ImagePackageStatus.uploading,
        ].includes(data.status)
      ) {
        return false;
      }
      return true;
    });

    this.dataLockGrid.dataReady(
      this.filteredDataLockData,
      this.dataLockColumns,
      this.dataLockColumnHeaders
    );
  }

  public async lockData() {
    this.lowyConfirmDatalockModal.open(this.dataLockToDate);
  }

  public async lockDataConfirmed() {
    // all items in range must be submitted
    for (let i = 0; i < this.filteredDataLockData.length; i++) {
      if (
        ![
          ImagePackageStatus.submitted,
          ImagePackageStatus.notGrading,
          ImagePackageStatus.uploading,
          "package incomplete",
        ].includes(this.filteredDataLockData[i]["status"])
      ) {
        this.toastService.throwToast(
          "All image packages in range must be submitted before Data Lock"
        );
        return;
      }
    }

    this.loadingSpinnerService.start();
    try {
      const imagePackageIds = _.map(
        this.filteredDataLockData,
        (imagePackage) => imagePackage.imagePackageId
      );
      await this.gradingService.lockData(imagePackageIds);
      this.loadingSpinnerService.stop();
      this.toastService.throwToast("Data Lock successful");

      this.setDataLockDates();
      this.updateReportCounts();
      this.viewReport(this.allReports.dataLock);
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast(
        err && err.message ? err.message : "Something went wrong"
      );
    }
  }

  public async showOnlyIncompleteDataLockToggled(showAll: boolean) {
    this.showOnlyIncompleteDataLock = showAll;
    this.filterDataLockData();
  }

  public viewGrading(imagePackage: ImagePackage) {
    this.router.navigate([
      "/grading",
      imagePackage.patientId,
      imagePackage.visitId,
      imagePackage.studyId,
    ]);
  }

  private setDataLockDates() {
    this.dataLockToDate = moment();
    this.dataLockGrid.toDate = moment().toISOString();
  }

  private viewRejectionReason(entry) {
    this.lowyRejectUpload.viewReasons(entry);
  }

  public async downloadAllRecords(downloadType: string) {
    this.loadingSpinnerService.start();
    let data;

    switch (this.currentReport) {
      case "readyToGrade":
      case "escalatedToSrGrader":
      case "escalatedToClinicianReview":
        data = this.readyToGradeGrid.checkedItems();
        await this.readyToGradeGrid.downloadFiles(downloadType, data);
        break;
      case "adjudicationQueue":
        data = this.adjudicateGrid.checkedItems();
        await this.adjudicateGrid.downloadFiles(downloadType, data);
        break;
      case "packageManagementQueue":
        data = this.packageManagementGrid.checkedItems();
        await this.packageManagementGrid.downloadFiles(downloadType, data);
        break;
      case "pendingApprovals":
        data = this.approvalGrid.checkedItems();
        await this.approvalGrid.downloadFiles(downloadType, data);
        break;
      case "dataLock":
        data = this.dataLockGrid.checkedItems();
        await this.dataLockGrid.downloadFiles(downloadType, data);
        break;
      default:
        data = this.reportGrid.checkedItems();
        await this.reportGrid.downloadFiles(downloadType, data);
        break;
    }

    this.loadingSpinnerService.stop();
  }
}

class Report {
  id: string;
  name: string;
  icon: string;
  count?: number;
  group: string;
}



// WEBPACK FOOTER //
// ./src/app/home/reports/reports.component.ts